import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getStudentTutorAnalytics } from '../api/tutorAnalyticsApi';
import type { TutorAnalyticsSummary } from '../types/tutorAnalytics';
import {
  mapTutorAnalyticsError,
  normalizeTutorAnalyticsSummary,
  useTutorAnalyticsSummary,
} from '../../modules/student-dashboard/hooks/useTutorAnalyticsSummary';

interface UseTutorAnalyticsOptions {
  studentId?: string;
  initialDays?: number;
  /** When true, skip network fetch (e.g. parent already loaded analytics). */
  skip?: boolean;
}

interface UseTutorAnalyticsReturn {
  data: TutorAnalyticsSummary | null;
  loading: boolean;
  error: string | null;
  days: number;
  setDays: (days: number) => void;
  refetch: () => void;
}

/** Teacher/student analytics hook. Student self-view delegates to canonical dashboard summary query. */
export function useTutorAnalytics(
  opts: UseTutorAnalyticsOptions = {},
): UseTutorAnalyticsReturn {
  const { studentId, initialDays = 30, skip = false } = opts;
  const [days, setDays] = useState(initialDays);

  const selfSummary = useTutorAnalyticsSummary(days, { enabled: !skip });

  const teacherQuery = useQuery<TutorAnalyticsSummary, Error>({
    queryKey: ['tutor', 'analytics', 'student-summary', studentId, days],
    queryFn: async () => {
      const result = await getStudentTutorAnalytics(studentId!, days);
      return normalizeTutorAnalyticsSummary(result);
    },
    enabled: Boolean(studentId) && !skip,
    staleTime: 5 * 60 * 1000,
  });

  const handleSetDays = useCallback((newDays: number) => {
    setDays(newDays);
  }, []);

  if (studentId) {
    return {
      data: teacherQuery.data ?? null,
      loading: teacherQuery.isLoading,
      error: teacherQuery.error ? mapTutorAnalyticsError(teacherQuery.error) : null,
      days,
      setDays: handleSetDays,
      refetch: () => void teacherQuery.refetch(),
    };
  }

  return {
    data: selfSummary.data,
    loading: selfSummary.loading,
    error: selfSummary.error,
    days,
    setDays: handleSetDays,
    refetch: selfSummary.refetch,
  };
}
